// [codex 2026-06-21] Shell side rails now push main content and use square connected edges.
// [Codex 2026-07-05] CAO history shell rail now keeps an exit state so it slides instead of snapping.
// [Codex 2026-07-03] Shell routes quick annotate context into the CAO PiP.
// [claude-code 2026-05-05] Shell resiliency + polish: overflow-safe root container and Strategium housing aligned with sidebar visual language.
// [claude-code 2026-03-11] Track 4: MC overhaul — no Panels header, collapse in MC header, 50/50 flex, gear menu
// [claude-code 2026-03-11] T3d: removed auto-enable from platform dropdown — power controlled via dedicated button only
// [claude-code 2026-03-20] S3:T4c: Linked Strategium ↔ RiskFlow collapse — both expand/collapse together
// [claude-code 2026-03-22] Replaced "The Tape" in Castra with RiskFlowMini (same as non-iFrame Strategium)
// [claude-code 2026-03-31] S12-T2: Added Documents tab (TipTap editor)
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { ChevronLeft } from "lucide-react";
import type { IVScoreResponse } from "../../types/market-data";
import { TopHeader } from "./TopHeader";
import { NavSidebar } from "./NavSidebar";
import { MinimalTapeWidget } from "../feed/MinimalTapeWidget";
import { TradingBrowser, type TradingPlatform } from "../TradingBrowser";
import { TimelineOverlay, TimelineToggleButton } from "./TimelineOverlay";
import { FloatingWidget } from "./FloatingWidget";
import { useBackend } from "../../lib/backend";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { EmotionalResonanceMonitor } from "../mission-control/EmotionalResonanceMonitor";
// [claude-code 2026-04-23] S30-T2: BlindspotsWidget retired from Strategium — promoted to Performance tab.
import { WeeklyPerformanceWidget } from "../mission-control/WeeklyPerformanceWidget";
import { AccountTrackerWidget } from "../mission-control/AccountTrackerWidget";
import { AlgoStatusWidget } from "../mission-control/AlgoStatusWidget";
import { DeskThemeWidget } from "../mission-control/DeskThemeWidget";
import { PanelNotificationWidget } from "./PanelNotificationWidget";
import { MinimalERMeter } from "../MinimalERMeter";
// [claude-code 2026-04-03] S14-T5: Scriptorium standalone tab removed — knowledge archive accessed via Apparatus dropdown
import { SectionBreadcrumb } from "./SectionBreadcrumb";
import RiskFlowMini from "../RiskFlowMini";
import { useRiskFlow } from "../../contexts/RiskFlowContext";
import { SearchModal } from "../search/SearchModal";
import { useSettings } from "../../contexts/SettingsContext";
import {
  PsychAssistDockable,
  type PsychAssistDockTarget,
} from "./PsychAssistDockable";
import { EconCountdownWidget } from "./EconCountdownWidget";
import { useEconHeaderCountdown } from "../../hooks/useEconHeaderCountdown";
import { getEconHeaderCountdownCandidate } from "../../lib/econ-header-countdown";
import { buildPerformanceEconEventModel } from "../journal/performance-edgecaster/performance-econ-event-model";
// [claude-code 2026-04-20] S21: Voice layer — Performance chat button + app-wide agent popup
// [claude-code 2026-04-24] PerformanceChatButton retired — heading-toolbar chat
// button had no clear purpose; the orb is the single voice trigger now.
// import { PerformanceChatButton } from "../performance/PerformanceChatButton";
import { FooterToolbar } from "./FooterToolbar";
import { EmbeddedBrowserFrame } from "./EmbeddedBrowserFrame";
import { ScheduleProvider } from "../../contexts/ScheduleContext";
import { YouTubeMiniplayerProvider } from "../../contexts/YouTubeMiniplayerContext";
// [claude-code 2026-04-03] S14-T5: Removed DocumentsView, SharedMemoryPanel, ResearchBoard standalone imports — now in ConsiliumHub
import { FirstTimeTour } from "../onboarding/FirstTimeTour";
import { WhatsNewButton } from "../onboarding/WhatsNewButton";
// Agent provider controls live in Settings.
import { SessionCountdownWidget } from "../mission-control/SessionCountdownWidget";
import { RegimeMini } from "../mission-control/RegimeMini";
import { MiniProposalCard } from "../mission-control/MiniProposalCard";
import { SessionCalendarMini } from "../mission-control/SessionCalendarMini";
import { useDND } from "../../contexts/DNDContext";
import { TabRenderer } from "./TabRenderer";
import { MissionControlContent } from "./MissionControlContent";
import { ChatPanel } from "./ChatPanel";
import { CaoConversationHistoryRail } from "../chat/CaoConversationHistoryRail";
import { CaoPipComposer } from "../chat/pip/CaoPipComposer";
import { YouTubeMiniplayer } from "./YouTubeMiniplayer";
import { MobileUnderlayDrawer } from "./MobileUnderlayDrawer";
import { MobileQuickFooter } from "./MobileQuickFooter";
import { WeeklyWalkthroughCue } from "../walkthroughs/WeeklyWalkthroughCue";
// [claude-code 2026-04-03] S14-T6: Removed PeerCarousel + PeerOnboarding — team status now in footer panel
// TeamOnboarding re-wired into TeamPanel behind auth gate (2026-04-11)
// Voice lives in the app-native ProxVoice surface.
import {
  DEFAULT_MISSION_WIDGET_ORDER,
  getMissionWidgetOrder,
  setMissionWidgetOrder,
  getMissionWidgetVisibility,
  setMissionWidgetVisibility,
  getStrategiumPaneMode,
  setStrategiumPaneMode,
  type MissionWidgetId,
  type StrategiumPaneMode,
} from "../../lib/layoutOrderStorage";
import { StrategiumPeekBar } from "./StrategiumPeekBar";
import { useKeyboardShortcuts } from "../../hooks/useKeyboardShortcuts";
import { useQuickAnnotateDock } from "../../hooks/useQuickAnnotateDock";
import { useLayoutState } from "../../hooks/useLayoutState";
import { useBrowserTransition } from "../../hooks/useBrowserTransition";
import { useWeeklyWalkthroughs } from "../../hooks/useWeeklyWalkthroughs";
import {
  buildSurfaceCapabilities,
  resolveSurfaceTab,
  type SurfaceNavTab,
} from "../../lib/surface-capabilities";
import { activeInstrumentFromSettings } from "../../lib/instruments";
import {
  resolveShellRailGeometry,
  type ShellRailDescriptor,
} from "../../lib/shell-rail-geometry";
import { shellRailDescriptorFor } from "../../lib/shell-rail-registry";
import { DeveloperCaoBridge } from "../scouts/DeveloperCaoBridge";
import { DeveloperDebriefPopup } from "../scouts/DeveloperDebriefPopup";
import { NarrativeFlowDesktopBridge } from "../narrative/NarrativeFlowDesktopBridge";
import { GlassFilter } from "../ui/liquid-glass";
import { RiskFlowAlertPresenter } from "../notifications/RiskFlowAlertPresenter";
import { NativeTitleTooltipBridge } from "../motion/native-title-tooltip-bridge";
import { setRiskFlowAlertSurfaceActive } from "../../lib/riskflow-alert-surface";

type NavTab =
  | "feed"
  | "analysis"
  | "riskflow"
  | "dashboard"
  | "econ"
  | "narrative"
  | "apparatus"
  | "scouts"
  | "performance"
  | "proposals"
  | "desk-ops"
  | "settings";
type LayoutOption = "tickers-only" | "combined";
type MobileConsiliumView = "chat" | "forum" | "arbitrum";
type MobileConsiliumViewInput =
  MobileConsiliumView | "war-room" | "narrativeflow";

const MOBILE_CONSILIUM_VIEW_STORAGE_KEY = "fintheon:mobile-consilium-view";

function normalizeMobileConsiliumView(
  view: MobileConsiliumViewInput,
): MobileConsiliumView {
  return view === "war-room" || view === "narrativeflow" ? "forum" : view;
}

function normalizeMobileConsiliumViewValue(
  view: unknown,
): MobileConsiliumView | null {
  if (view === "chat" || view === "forum" || view === "arbitrum") return view;
  if (view === "forum" || view === "war-room" || view === "narrativeflow")
    return "forum";
  return null;
}

function rememberMobileConsiliumView(view: MobileConsiliumView) {
  try {
    window.sessionStorage.setItem(MOBILE_CONSILIUM_VIEW_STORAGE_KEY, view);
  } catch {
    /* session storage can be blocked */
  }
}

// [claude-code 2026-04-19] Last-visited tab persistence — restores the user's
// prior surface on sign-in / app restart so we don't dump them back at the
// default dashboard. Invalid / legacy values fall back to "dashboard".
const LAST_ROUTE_KEY = "fintheon:last-route:v1";
const VALID_TABS: ReadonlySet<NavTab> = new Set<NavTab>([
  "feed",
  "analysis",
  "riskflow",
  "dashboard",
  "econ",
  "narrative",
  "apparatus",
  "scouts",
  "performance",
  "proposals",
  "desk-ops",
  "settings",
]);
const CAO_HISTORY_RAIL_EXIT_MS = 340;

function routeStorageKey(userId: string): string {
  return userId ? `${LAST_ROUTE_KEY}:${userId}` : LAST_ROUTE_KEY;
}

function readLastRoute(userId = ""): NavTab {
  if (typeof window === "undefined") return "dashboard";
  const urlTab = readInitialRouteFromUrl();
  if (urlTab) return urlTab;
  try {
    const raw =
      window.localStorage.getItem(routeStorageKey(userId)) ??
      window.localStorage.getItem(LAST_ROUTE_KEY);
    if (raw && VALID_TABS.has(raw as NavTab)) return raw as NavTab;
  } catch {
    // ignore
  }
  return "dashboard";
}

function readInitialRouteFromUrl(): NavTab | null {
  try {
    const tab = new URLSearchParams(window.location.search).get("tab");
    return tab && VALID_TABS.has(tab as NavTab) ? (tab as NavTab) : null;
  } catch {
    return null;
  }
}

function readInitialMobileConsiliumView(): MobileConsiliumView {
  if (typeof window === "undefined") return "chat";
  const urlView = readMobileConsiliumViewFromUrl();
  if (urlView) return urlView;
  try {
    return (
      normalizeMobileConsiliumViewValue(
        window.sessionStorage.getItem(MOBILE_CONSILIUM_VIEW_STORAGE_KEY),
      ) ?? "chat"
    );
  } catch {
    return "chat";
  }
}

function readMobileConsiliumViewFromUrl(): MobileConsiliumView | null {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("forumInvite") === "1") return "forum";
    return normalizeMobileConsiliumViewValue(
      params.get("view") ??
        params.get("consilium") ??
        params.get("consiliumView"),
    );
  } catch {
    return null;
  }
}

function writeLastRoute(tab: NavTab, userId = ""): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(routeStorageKey(userId), tab);
  } catch {
    // ignore
  }
}

// TEAM_ONBOARDED_KEY removed

function normalizeOrder<T extends string>(
  order: T[],
  defaults: readonly T[],
): T[] {
  const deduped = order.filter(
    (id, idx) => defaults.includes(id) && order.indexOf(id) === idx,
  );
  const missing = defaults.filter((id) => !deduped.includes(id));
  return [...deduped, ...missing];
}

export function MainLayout() {
  return <MainLayoutInner />;
}

// Responsive tier breakpoints for progressive app-width compaction
const COMPACT_MODERATE_BP = 1600;
const COMPACT_SEVERE_BP = 1360;

function readAppShellWidth() {
  if (typeof window === "undefined") return 1600;
  const shell =
    document.querySelector<HTMLElement>(".fintheon-app-shell") ??
    document.getElementById("root") ??
    document.documentElement;
  const rectWidth = shell.getBoundingClientRect().width;
  return Math.round(
    rectWidth || document.documentElement.clientWidth || window.innerWidth,
  );
}

// Main layout component - no authentication needed
function MainLayoutInner() {
  const {
    iframeUrls,
    defaultLayout,
    defaultPlatform,
    developerSettings,
    selectedInstrument,
    selectedSymbol,
  } = useSettings();
  const activeInstrument = activeInstrumentFromSettings({
    selectedInstrument,
    selectedSymbol,
  });
  const { theme, zenModeEnabled } = useTheme();
  const { isAuthenticated, userId } = useAuth();
  const isStone = theme.name === "solvys-stone";
  const { setAutoDnd, flushQueue, toggleManualDnd } = useDND();
  const [activeTab, setActiveTab] = useState<NavTab>(() =>
    readLastRoute(userId),
  );
  const [caoPipOpen, setCaoPipOpen] = useState(false);
  const [caoHistoryRailOpen, setCaoHistoryRailOpen] = useState(false);
  const [caoHistoryRailMounted, setCaoHistoryRailMounted] = useState(false);
  const caoHistoryRailOpenFrameRef = useRef<number | null>(null);
  const caoHistoryRailCloseTimerRef = useRef<number | null>(null);

  const clearCaoHistoryRailOpenFrame = useCallback(() => {
    if (typeof window === "undefined") return;
    if (caoHistoryRailOpenFrameRef.current == null) return;
    window.cancelAnimationFrame(caoHistoryRailOpenFrameRef.current);
    caoHistoryRailOpenFrameRef.current = null;
  }, []);

  const clearCaoHistoryRailCloseTimer = useCallback(() => {
    if (typeof window === "undefined") return;
    if (caoHistoryRailCloseTimerRef.current == null) return;
    window.clearTimeout(caoHistoryRailCloseTimerRef.current);
    caoHistoryRailCloseTimerRef.current = null;
  }, []);

  const openCaoHistoryRail = useCallback(() => {
    clearCaoHistoryRailCloseTimer();
    clearCaoHistoryRailOpenFrame();
    setCaoHistoryRailMounted(true);
    if (typeof window === "undefined") {
      setCaoHistoryRailOpen(true);
      return;
    }
    caoHistoryRailOpenFrameRef.current = window.requestAnimationFrame(() => {
      caoHistoryRailOpenFrameRef.current = null;
      setCaoHistoryRailOpen(true);
    });
  }, [clearCaoHistoryRailCloseTimer, clearCaoHistoryRailOpenFrame]);

  const closeCaoHistoryRail = useCallback(() => {
    clearCaoHistoryRailOpenFrame();
    setCaoHistoryRailOpen(false);
    clearCaoHistoryRailCloseTimer();
    if (typeof window === "undefined") {
      setCaoHistoryRailMounted(false);
      return;
    }
    caoHistoryRailCloseTimerRef.current = window.setTimeout(() => {
      caoHistoryRailCloseTimerRef.current = null;
      setCaoHistoryRailMounted(false);
    }, CAO_HISTORY_RAIL_EXIT_MS);
  }, [clearCaoHistoryRailCloseTimer, clearCaoHistoryRailOpenFrame]);

  const toggleCaoHistoryRail = useCallback(() => {
    if (caoHistoryRailOpen) {
      closeCaoHistoryRail();
      return;
    }
    openCaoHistoryRail();
  }, [caoHistoryRailOpen, closeCaoHistoryRail, openCaoHistoryRail]);

  useEffect(
    () => () => {
      clearCaoHistoryRailOpenFrame();
      clearCaoHistoryRailCloseTimer();
    },
    [clearCaoHistoryRailCloseTimer, clearCaoHistoryRailOpenFrame],
  );

  // [codex 2026-06-22] Tiered responsive compaction follows the rendered app
  // shell width, not a device/user-agent assumption.
  const [appWidth, setAppWidth] = useState(readAppShellWidth);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const updateAppWidth = () => setAppWidth(readAppShellWidth());
    updateAppWidth();
    const shell =
      document.querySelector<HTMLElement>(".fintheon-app-shell") ??
      document.getElementById("root");
    const observer =
      shell && "ResizeObserver" in window
        ? new ResizeObserver(updateAppWidth)
        : null;
    if (shell) observer?.observe(shell);
    window.addEventListener("resize", updateAppWidth);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", updateAppWidth);
    };
  }, []);
  const compactLevel: 0 | 1 | 2 =
    appWidth < COMPACT_SEVERE_BP ? 2 : appWidth < COMPACT_MODERATE_BP ? 1 : 0;
  const surfaceCapabilities = useMemo(
    () => buildSurfaceCapabilities(appWidth),
    [appWidth],
  );
  const {
    topStepXEnabled,
    browserTransitioning,
    browserVisible,
    selectedPlatform,
    setSelectedPlatform,
    secondaryPlatform,
    setSecondaryPlatform,
    splitBrowserView,
    setSplitBrowserView,
    handleBrowserToggle,
    handleBrowserEnable,
  } = useBrowserTransition({ defaultPlatform });
  const iframeModeActive =
    surfaceCapabilities.allowCustomIframes && topStepXEnabled;
  const mobileDrawerMode =
    surfaceCapabilities.navigationMode === "underlay-drawer" &&
    !iframeModeActive;

  useEffect(() => {
    setRiskFlowAlertSurfaceActive(
      "browser",
      iframeModeActive && browserVisible,
    );
    return () => setRiskFlowAlertSurfaceActive("browser", false);
  }, [browserVisible, iframeModeActive]);

  useEffect(() => {
    if (!mobileDrawerMode) setMobileDrawerOpen(false);
  }, [mobileDrawerMode]);

  useEffect(() => {
    setMobileDrawerOpen(false);
  }, [activeTab]);

  const {
    layoutEditMode,
    setLayoutEditMode,
    missionControlCollapsed,
    setMissionControlCollapsed,
    tapeCollapsed,
    setTapeCollapsed,
    combinedPanelCollapsed,
    setCombinedPanelCollapsed,
    combinedTapeCollapsed,
    setCombinedTapeCollapsed,
    layoutOption,
    setLayoutOption,
    missionControlPosition,
    setMissionControlPosition,
    tapePosition,
    setTapePosition,
    riskFlowCollapsed,
    setRiskFlowCollapsed,
    sidebarOverlayVisible,
    setSidebarOverlayVisible,
  } = useLayoutState({
    topStepXEnabled: iframeModeActive,
    defaultLayout,
    setAutoDnd,
    flushQueue,
  });

  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [mobileConsiliumView, setMobileConsiliumView] =
    useState<MobileConsiliumView>(readInitialMobileConsiliumView);
  const [mobileChatComposerFocused, setMobileChatComposerFocused] =
    useState(false);
  const mobileEdgeSwipeRef = useRef<{ x: number; y: number } | null>(null);
  const [showRefinement, setShowRefinement] = useState(false);
  const [timelineOverlayOpen, setTimelineOverlayOpen] = useState(false);
  const refinementEnabled =
    typeof window !== "undefined" &&
    localStorage.getItem("fintheon-refinement-enabled") === "true";
  const [tabTransitioning, setTabTransitioning] = useState(false);
  const [prevTab, setPrevTab] = useState<NavTab | null>(null);
  const [ivData, setIvData] = useState<IVScoreResponse | null>(null);
  const [ivLoading, setIvLoading] = useState(true);
  const [showMissionControlNotification, setShowMissionControlNotification] =
    useState(false);
  const [showTapeNotification, setShowTapeNotification] = useState(false);
  const [combinedPanelErScore, setCombinedPanelErScore] = useState(0);
  const [combinedPanelPnl, setCombinedPanelPnl] = useState(0);
  const [combinedPanelAlgoEnabled, setCombinedPanelAlgoEnabled] =
    useState(false);
  const [showChat, setShowChat] = useState(false);

  const popOutCaoChat = useCallback(() => {
    setShowChat(false);
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      setCaoPipOpen(true);
      return;
    }
    window.setTimeout(() => setCaoPipOpen(true), 160);
  }, []);

  const restoreCaoRail = useCallback(() => {
    setCaoPipOpen(false);
    setShowChat(true);
  }, []);

  const openCaoPip = useCallback(() => {
    setCaoPipOpen(true);
  }, []);

  const openMobileCaoRail = useCallback(() => {
    setShowChat(true);
  }, []);

  useEffect(() => {
    const handler = (event: Event) => {
      const focused = Boolean(
        (event as CustomEvent<{ focused?: boolean }>).detail?.focused,
      );
      setMobileChatComposerFocused(focused);
    };
    window.addEventListener("fintheon:chat-composer-focus", handler);
    return () =>
      window.removeEventListener("fintheon:chat-composer-focus", handler);
  }, []);

  useEffect(() => {
    if (
      mobileDrawerMode &&
      activeTab === "analysis" &&
      mobileConsiliumView === "chat"
    ) {
      return;
    }
    setMobileChatComposerFocused(false);
  }, [activeTab, mobileConsiliumView, mobileDrawerMode]);

  const { quickAnnotateContext, closeQuickAnnotate } = useQuickAnnotateDock({
    isMobile: surfaceCapabilities.isMobile,
    onOpenDesktop: openCaoPip,
    onOpenMobile: openMobileCaoRail,
  });

  useEffect(() => {
    if (!iframeModeActive) setCaoPipOpen(false);
  }, [iframeModeActive]);
  const openBulletinInbox = useCallback(() => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(
      new CustomEvent("fintheon:open-bulletin-section", {
        detail: { section: "inbox" },
      }),
    );
  }, []);
  const handleChatAlert = useCallback(
    (alert: {
      headline: string;
      summary?: string | null;
      source?: string;
      ivScore?: number | null;
      publishedAt?: string;
    }) => {
      if (surfaceCapabilities.isMobile) {
        window.dispatchEvent(
          new CustomEvent("fintheon:navigate-tab", {
            detail: { tab: "analysis" },
          }),
        );
        window.setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent("fintheon:consilium-lite-view", {
              detail: { view: "chat" },
            }),
          );
        }, 40);
      } else {
        setShowChat(true);
      }
      const parts: string[] = [];
      parts.push(`[RiskFlow Context]`);
      parts.push(`Headline: ${alert.headline}`);
      if (alert.source) parts.push(`Source: ${alert.source}`);
      if (alert.ivScore != null)
        parts.push(`IV Score: ${alert.ivScore.toFixed(1)}`);
      if (alert.publishedAt) {
        const ago = Math.round(
          (Date.now() - new Date(alert.publishedAt).getTime()) / 60000,
        );
        parts.push(`Time: ${ago}m ago`);
      }
      if (alert.summary && alert.summary !== alert.headline) {
        parts.push(`Summary: ${alert.summary}`);
      }
      window.dispatchEvent(
        new CustomEvent("fintheon:send-chat-text", {
          detail: { text: parts.join("\n") },
        }),
      );
    },
    [surfaceCapabilities.isMobile],
  );

  useEffect(() => {
    const openChatPanel = () => {
      if (surfaceCapabilities.isMobile) {
        window.dispatchEvent(
          new CustomEvent("fintheon:navigate-tab", {
            detail: { tab: "analysis" },
          }),
        );
        window.setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent("fintheon:consilium-lite-view", {
              detail: { view: "chat" },
            }),
          );
        }, 40);
        return;
      }
      setShowChat(true);
    };
    window.addEventListener("fintheon:open-chat-panel", openChatPanel);
    return () =>
      window.removeEventListener("fintheon:open-chat-panel", openChatPanel);
  }, [surfaceCapabilities.isMobile]);

  useEffect(() => {
    window.addEventListener(
      "fintheon:open-cao-history-rail",
      openCaoHistoryRail,
    );
    window.addEventListener(
      "fintheon:close-cao-history-rail",
      closeCaoHistoryRail,
    );
    window.addEventListener(
      "fintheon:toggle-cao-history-rail",
      toggleCaoHistoryRail,
    );
    return () => {
      window.removeEventListener(
        "fintheon:open-cao-history-rail",
        openCaoHistoryRail,
      );
      window.removeEventListener(
        "fintheon:close-cao-history-rail",
        closeCaoHistoryRail,
      );
      window.removeEventListener(
        "fintheon:toggle-cao-history-rail",
        toggleCaoHistoryRail,
      );
    };
  }, [closeCaoHistoryRail, openCaoHistoryRail, toggleCaoHistoryRail]);

  useEffect(() => {
    const open = () => {
      if (mobileDrawerMode) setMobileDrawerOpen(true);
    };
    window.addEventListener("fintheon:open-mobile-drawer", open);
    return () => {
      window.removeEventListener("fintheon:open-mobile-drawer", open);
    };
  }, [mobileDrawerMode]);

  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showYouTubeMiniplayer, setShowYouTubeMiniplayer] = useState(() => {
    try {
      return localStorage.getItem("fintheon:yt-miniplayer-open") === "true";
    } catch {
      return false;
    }
  });
  const [missionWidgetOrder, setMissionWidgetOrderState] = useState<
    MissionWidgetId[]
  >(() =>
    normalizeOrder(getMissionWidgetOrder(), DEFAULT_MISSION_WIDGET_ORDER),
  );
  const [missionWidgetVisibility, setMissionWidgetVisibilityState] = useState<
    Record<MissionWidgetId, boolean>
  >(getMissionWidgetVisibility);
  // [claude-code 2026-04-25] Strategium fullscreen modes (feedOnly / widgetsOnly) retired
  // along with the maximize-RiskFlow overlay button. Any persisted mode is normalized to
  // "balanced" on mount so users coming in with stale state aren't stranded in a mode
  // there's no longer a UI control to leave.
  const [strategiumPaneMode, setStrategiumPaneModeState] =
    useState<StrategiumPaneMode>(() => {
      const m = getStrategiumPaneMode();
      return m === "balanced" ? m : "balanced";
    });
  const updateStrategiumPaneMode = useCallback((mode: StrategiumPaneMode) => {
    setStrategiumPaneModeState(mode);
    setStrategiumPaneMode(mode);
  }, []);
  useEffect(() => {
    if (strategiumPaneMode !== "balanced") {
      updateStrategiumPaneMode("balanced");
    }
    // run once on mount to clear stale persisted modes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // [claude-code 2026-04-26] Header PanelToggleGroup right-button wiring.
  // Toggles the Strategium right-panel collapse + broadcasts state back so the
  // header icon's filled-right indicator stays in sync.
  useEffect(() => {
    const onToggle = () => setMissionControlCollapsed((prev: boolean) => !prev);
    window.addEventListener("fintheon:toggle-strategium", onToggle);
    return () =>
      window.removeEventListener("fintheon:toggle-strategium", onToggle);
  }, [setMissionControlCollapsed]);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("fintheon:strategium-state", {
        detail: { open: !missionControlCollapsed },
      }),
    );
  }, [missionControlCollapsed]);
  const [psychAssistTarget, setPsychAssistTarget] =
    useState<PsychAssistDockTarget>(() => {
      try {
        return (
          (localStorage.getItem(
            "fintheon:psychassist-target:v1",
          ) as PsychAssistDockTarget) || "floating"
        );
      } catch {
        return "floating";
      }
    });
  useEffect(() => {
    try {
      localStorage.setItem("fintheon:psychassist-target:v1", psychAssistTarget);
    } catch {
      // ignore
    }
  }, [psychAssistTarget]);

  const { events: econHeaderEvents } = useEconHeaderCountdown();
  const [dismissedEconCountdownWindowId, setDismissedEconCountdownWindowId] =
    useState<string | null>(null);
  const econCountdownCandidate = useMemo(
    () => getEconHeaderCountdownCandidate(econHeaderEvents),
    [econHeaderEvents],
  );
  const latestReleasedEconEvent = useMemo(
    () => buildPerformanceEconEventModel(econHeaderEvents).latestReleasedEvent,
    [econHeaderEvents],
  );

  const showEconCountdown =
    Boolean(econCountdownCandidate) &&
    dismissedEconCountdownWindowId !== econCountdownCandidate?.dayPlanWindowId;

  useEffect(() => {
    setMissionWidgetOrderState((prev) =>
      normalizeOrder(prev, DEFAULT_MISSION_WIDGET_ORDER),
    );
  }, []);

  // Tab history for breadcrumb back/forward navigation
  const [tabHistory, setTabHistory] = useState<NavTab[]>(() => [activeTab]);
  const [historyIndex, setHistoryIndex] = useState(0);

  // Persist every tab change so the next boot lands on the same surface.
  useEffect(() => {
    writeLastRoute(activeTab, userId);
  }, [activeTab, userId]);

  useEffect(() => {
    const safeTab = resolveSurfaceTab(
      activeTab as SurfaceNavTab,
      surfaceCapabilities,
    ) as NavTab;
    if (safeTab !== activeTab) setActiveTab(safeTab);
  }, [activeTab, surfaceCapabilities]);

  const navigateTab = (tab: NavTab) => {
    const safeTab = resolveSurfaceTab(
      tab as SurfaceNavTab,
      surfaceCapabilities,
    ) as NavTab;
    // Trim forward history when navigating to a new tab
    const trimmed = tabHistory.slice(0, historyIndex + 1);
    const nextHistory =
      trimmed[trimmed.length - 1] === safeTab ? trimmed : [...trimmed, safeTab];
    const cappedHistory = mobileDrawerMode
      ? nextHistory.slice(-3)
      : nextHistory;
    setTabHistory(cappedHistory);
    setHistoryIndex(cappedHistory.length - 1);
    setActiveTab(safeTab);
  };
  const { activeWalkthrough, closeWalkthrough, replayWalkthrough } =
    useWeeklyWalkthroughs({
      navigateTab: (tab) => navigateTab(tab as NavTab),
    });

  const openConsiliumLiteView = (view: MobileConsiliumViewInput) => {
    const safeView = normalizeMobileConsiliumView(view);
    rememberMobileConsiliumView(safeView);
    setMobileConsiliumView(safeView);
    setShowRefinement(false);
    navigateTab("analysis");
    window.setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("fintheon:consilium-lite-view", {
          detail: { view: safeView },
        }),
      );
    }, 30);
  };

  useEffect(() => {
    const handler = (event: Event) => {
      const view = (event as CustomEvent<{ view?: MobileConsiliumViewInput }>)
        .detail?.view;
      const safeView = normalizeMobileConsiliumViewValue(view);
      if (safeView) {
        rememberMobileConsiliumView(safeView);
        setMobileConsiliumView(safeView);
      }
    };
    window.addEventListener("fintheon:consilium-lite-view-changed", handler);
    return () =>
      window.removeEventListener(
        "fintheon:consilium-lite-view-changed",
        handler,
      );
  }, []);

  useEffect(() => {
    const jumpToNarrativeFlow = () => {
      try {
        window.localStorage.setItem(
          "fintheon:pending-consilium-surface",
          "narrativeflow",
        );
      } catch {
        /* ignore */
      }
      setShowRefinement(false);
      if (iframeModeActive) handleBrowserToggle();
      const safeView = normalizeMobileConsiliumView("narrativeflow");
      rememberMobileConsiliumView(safeView);
      setMobileConsiliumView(safeView);
      navigateTab("analysis");
      window.setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent("fintheon:consilium-lite-view", {
            detail: { view: safeView },
          }),
        );
        window.dispatchEvent(new Event("fintheon:open-narrativeflow"));
      }, 80);
    };
    window.addEventListener(
      "fintheon:jump-to-narrativeflow",
      jumpToNarrativeFlow,
    );
    return () =>
      window.removeEventListener(
        "fintheon:jump-to-narrativeflow",
        jumpToNarrativeFlow,
      );
  }, [handleBrowserToggle, historyIndex, tabHistory, iframeModeActive]);

  useEffect(() => {
    const handler = (event: Event) => {
      const tab = (event as CustomEvent<{ tab?: NavTab }>).detail?.tab;
      if (tab) navigateTab(tab);
    };
    window.addEventListener("fintheon:navigate-tab", handler);
    return () => window.removeEventListener("fintheon:navigate-tab", handler);
  }, [tabHistory, historyIndex]);

  useEffect(() => {
    const handler = (event: Event) => {
      const platform = (event as CustomEvent<{ platform?: TradingPlatform }>)
        .detail?.platform;
      if (platform) setSelectedPlatform(platform);
      if (!iframeModeActive) handleBrowserEnable();
    };
    window.addEventListener("fintheon:trading-browser-open", handler);
    return () =>
      window.removeEventListener("fintheon:trading-browser-open", handler);
  }, [handleBrowserEnable, iframeModeActive, setSelectedPlatform]);

  const goBack = () => {
    if (historyIndex > 0) {
      const mobileHistory = mobileDrawerMode
        ? tabHistory.slice(Math.max(0, historyIndex - 2), historyIndex + 1)
        : tabHistory;
      const mobileIndex = mobileDrawerMode
        ? mobileHistory.length - 1
        : historyIndex;
      const newIdx = mobileIndex - 1;
      setTabHistory(mobileHistory);
      setHistoryIndex(newIdx);
      setActiveTab(mobileHistory[newIdx]);
    }
  };

  const goForward = () => {
    if (historyIndex < tabHistory.length - 1) {
      const newIdx = historyIndex + 1;
      setHistoryIndex(newIdx);
      setActiveTab(tabHistory[newIdx]);
    }
  };

  const handleShellTouchStart = useCallback(
    (event: React.TouchEvent<HTMLDivElement>) => {
      if (!mobileDrawerMode || mobileDrawerOpen || historyIndex <= 0) {
        mobileEdgeSwipeRef.current = null;
        return;
      }
      const touch = event.touches[0];
      if (!touch || touch.clientX > 24) {
        mobileEdgeSwipeRef.current = null;
        return;
      }
      mobileEdgeSwipeRef.current = { x: touch.clientX, y: touch.clientY };
    },
    [historyIndex, mobileDrawerMode, mobileDrawerOpen],
  );

  const handleShellTouchEnd = useCallback(
    (event: React.TouchEvent<HTMLDivElement>) => {
      const start = mobileEdgeSwipeRef.current;
      mobileEdgeSwipeRef.current = null;
      if (!start || !mobileDrawerMode || mobileDrawerOpen) return;
      const touch = event.changedTouches[0];
      if (!touch) return;
      const deltaX = touch.clientX - start.x;
      const deltaY = Math.abs(touch.clientY - start.y);
      if (deltaX > 58 && deltaY < 42) goBack();
    },
    [mobileDrawerMode, mobileDrawerOpen, goBack],
  );

  const backend = useBackend();
  const { alerts: riskFlowAlerts, isSeen } = useRiskFlow();
  useKeyboardShortcuts({
    navigateTab: navigateTab as (tab: string) => void,
    setShowSearchModal,
    setShowYouTubeMiniplayer,
    setNotificationCenterOpen: () => {
      window.dispatchEvent(new CustomEvent("fintheon:close-bulletin"));
    },
    toggleManualDnd,
  });

  // Fetch blended IV score from backend for floating widget
  useEffect(() => {
    const fetchIVScore = async () => {
      try {
        const data = await backend.marketData.getIVScore(activeInstrument);
        setIvData(data);
      } catch (error) {
        console.warn("[IV] Failed to fetch IV score:", error);
      } finally {
        setIvLoading(false);
      }
    };

    fetchIVScore();
    const interval = setInterval(fetchIVScore, 300000);
    return () => clearInterval(interval);
  }, [activeInstrument, backend]);

  // Fetch account data for combined panel collapsed state (waits for auth)
  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchAccount = async () => {
      try {
        const account = await backend.account.get();
        setCombinedPanelPnl(account.dailyPnl);
        setCombinedPanelAlgoEnabled(account.autoTrade || false);
      } catch (err) {
        console.warn("Failed to fetch account:", err);
      }
    };
    fetchAccount();
    const interval = setInterval(fetchAccount, 5000);
    return () => clearInterval(interval);
  }, [backend, isAuthenticated]);

  // Listen for ER score updates for combined panel
  useEffect(() => {
    const handleERUpdate = (event: CustomEvent<number>) => {
      setCombinedPanelErScore(event.detail);
    };
    window.addEventListener("erScoreUpdate", handleERUpdate as EventListener);
    return () => {
      window.removeEventListener(
        "erScoreUpdate",
        handleERUpdate as EventListener,
      );
    };
  }, []);

  // Normalize ER score from -10 to 10 range to 0-1 range for display
  const normalizedCombinedPanelResonance = Math.max(
    0,
    Math.min(1, (combinedPanelErScore + 10) / 20),
  );

  const handleTabChange = (tab: NavTab) => {
    if (tab === activeTab || tabTransitioning) return;
    setTabTransitioning(true);
    setPrevTab(activeTab);
    setTimeout(() => {
      navigateTab(tab);
      setTimeout(() => {
        setTabTransitioning(false);
        setPrevTab(null);
      }, 50);
    }, 300);
  };

  const handleLogout = async () => {
    try {
      const { signOut } = await import("../../lib/supabase");
      await signOut();
      // Force reload to reset all state and show login screen
      window.location.reload();
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  // Determine layout based on TopStepX state and layout option
  const showMissionControl =
    iframeModeActive && missionControlPosition !== "floating";
  const showTape = iframeModeActive && tapePosition !== "floating";
  const showFloatingWidget =
    iframeModeActive && layoutOption === "tickers-only";
  const showCombinedPanel = iframeModeActive && layoutOption === "combined";
  const zenModeActive =
    zenModeEnabled || (iframeModeActive && layoutOption === "tickers-only");

  useEffect(() => {
    document.documentElement.dataset.zenMode = zenModeActive ? "true" : "false";
    document.body.dataset.fintheonZenMode = zenModeActive ? "true" : "false";
    document.body.classList.toggle("fintheon-zen-mode", zenModeActive);
    window.dispatchEvent(
      new CustomEvent("fintheon:zen-mode-change", {
        detail: { active: zenModeActive },
      }),
    );

    return () => {
      document.documentElement.dataset.zenMode = zenModeEnabled
        ? "true"
        : "false";
      document.body.dataset.fintheonZenMode = "false";
      document.body.classList.remove("fintheon-zen-mode");
      window.dispatchEvent(
        new CustomEvent("fintheon:zen-mode-change", {
          detail: { active: false },
        }),
      );
    };
  }, [zenModeActive, zenModeEnabled]);

  // Determine panel order based on position and layout option
  const leftPanels: React.ReactNode[] = [];
  const rightPanels: React.ReactNode[] = [];
  let strategiumRailVisible = false;

  if (caoHistoryRailMounted) {
    leftPanels.push(
      <CaoConversationHistoryRail
        key="cao-conversation-history"
        open={caoHistoryRailOpen}
        onClose={closeCaoHistoryRail}
      />,
    );
  }

  const handleMissionWidgetReorder = useCallback((order: MissionWidgetId[]) => {
    const normalized = normalizeOrder(order, DEFAULT_MISSION_WIDGET_ORDER);
    setMissionWidgetOrderState(normalized);
    setMissionWidgetOrder(normalized);
  }, []);

  const handleMissionWidgetToggleVisibility = useCallback(
    (id: MissionWidgetId) => {
      setMissionWidgetVisibilityState((prev) => {
        const next = { ...prev, [id]: !(prev[id] !== false) };
        setMissionWidgetVisibility(next);
        return next;
      });
    },
    [],
  );

  // [claude-code 2026-04-25] Reset Strategium widget order + visibility to defaults.
  // Wired to the t-dropdown reset button that fades in next to Pencil when edit mode is on.
  const handleMissionWidgetResetLayout = useCallback(() => {
    setMissionWidgetOrderState(DEFAULT_MISSION_WIDGET_ORDER);
    setMissionWidgetOrder(DEFAULT_MISSION_WIDGET_ORDER);
    const allOn: Record<MissionWidgetId, boolean> = {
      er: true,
      autopilot: true,
      regime: true,
      account: true,
      weekly: true,
      calendar: true,
      deskTheme: true,
    };
    setMissionWidgetVisibilityState(allOn);
    setMissionWidgetVisibility(allOn);
  }, []);

  const missionWidgetRegistry = useMemo(
    () => ({
      er: {
        id: "er" as const,
        label: "Emotional Resonance",
        node: (
          <EmotionalResonanceMonitor
            onERScoreChange={setCombinedPanelErScore}
          />
        ),
      },
      autopilot: {
        id: "autopilot" as const,
        label: "Autopilot",
        node: <AlgoStatusWidget />,
      },
      regime: {
        id: "regime" as const,
        label: "Regime Tracker",
        node: (
          <div className="space-y-2">
            <RegimeMini />
            <MiniProposalCard onExpand={() => handleTabChange("proposals")} />
          </div>
        ),
      },
      account: {
        id: "account" as const,
        label: "Account Tracker",
        node: <AccountTrackerWidget />,
      },
      weekly: {
        id: "weekly" as const,
        label: "Weekly Performance",
        node: <WeeklyPerformanceWidget />,
      },
      calendar: {
        id: "calendar" as const,
        label: "Session Calendar",
        node: <SessionCalendarMini />,
      },
      // [claude-code 2026-04-28] T3: Renamed Desk Theme -> Desk Plan in visible UI.
      // [claude-code 2026-04-27] S46.4/G: Desk Plan widget — pulls deskTheme
      // from /api/day-plan/today + tap-to-expand into the matching brief.
      deskTheme: {
        id: "deskTheme" as const,
        label: "Desk Plan",
        node: <DeskThemeWidget />,
      },
    }),
    [],
  );

  const orderedMissionWidgets = useMemo(() => {
    const normalized = normalizeOrder(
      missionWidgetOrder,
      DEFAULT_MISSION_WIDGET_ORDER,
    );
    return normalized
      .filter((id) => missionWidgetVisibility[id] !== false)
      .map((id) => missionWidgetRegistry[id]);
  }, [missionWidgetOrder, missionWidgetRegistry, missionWidgetVisibility]);

  // Full list (including hidden) for the arrange menu
  const allMissionWidgets = useMemo(() => {
    const normalized = normalizeOrder(
      missionWidgetOrder,
      DEFAULT_MISSION_WIDGET_ORDER,
    );
    return normalized.map((id) => ({
      id,
      label: missionWidgetRegistry[id].label,
    }));
  }, [missionWidgetOrder, missionWidgetRegistry]);

  const renderMissionControl = useCallback(
    (collapseFn?: () => void) => (
      <MissionControlContent
        orderedMissionWidgets={orderedMissionWidgets}
        allMissionWidgets={allMissionWidgets}
        missionWidgetVisibility={missionWidgetVisibility}
        onReorder={handleMissionWidgetReorder}
        onToggleVisibility={handleMissionWidgetToggleVisibility}
        onResetLayout={handleMissionWidgetResetLayout}
        collapseFn={collapseFn}
        editMode={layoutEditMode}
        onToggleEditMode={() => setLayoutEditMode(!layoutEditMode)}
      />
    ),
    [
      orderedMissionWidgets,
      allMissionWidgets,
      missionWidgetVisibility,
      handleMissionWidgetReorder,
      handleMissionWidgetToggleVisibility,
      handleMissionWidgetResetLayout,
      layoutEditMode,
      setLayoutEditMode,
    ],
  );

  // When TopStepX is enabled, render panels based on layout option
  if (surfaceCapabilities.allowStrategium && iframeModeActive) {
    if (layoutOption === "combined") {
      strategiumRailVisible = true;
      // Combined panel: Mission Control + The Tape in one scroll (split, no overlap)
      rightPanels.push(
        <div
          key="combined"
          className={`fintheon-side-surface fintheon-shell-side-panel fintheon-drawer-right border-y border-r border-[var(--fintheon-accent)]/20 bg-[var(--fintheon-surface)] transition-all duration-200 ${combinedPanelCollapsed ? "w-16" : "w-[min(380px,42vw)]"}`}
        >
          <div className="h-full flex flex-col">
            {combinedPanelCollapsed && (
              <div className="h-12 flex-shrink-0 flex items-center justify-center border-b border-[var(--fintheon-accent)]/20">
                <button
                  onClick={() => setCombinedPanelCollapsed(false)}
                  className="p-1.5 hover:bg-[var(--fintheon-accent)]/10 rounded transition-colors"
                >
                  <ChevronLeft className="w-4 h-4 text-[var(--fintheon-accent)]" />
                </button>
              </div>
            )}
            {!combinedPanelCollapsed && (
              <div className="flex-1 min-h-0 flex flex-col">
                {/* Mission Control: 50% height, no overflow into tape */}
                <section
                  className={`${combinedTapeCollapsed ? "flex-1" : "h-1/2"} min-h-0 overflow-y-auto border-b border-[var(--fintheon-accent)]/20`}
                >
                  <div className="p-3 h-full">
                    {renderMissionControl(() =>
                      setCombinedPanelCollapsed(true),
                    )}
                  </div>
                </section>
                {/* RiskFlow: 50% when expanded, 168px collapsed preview at bottom */}
                <section
                  className={`${combinedTapeCollapsed ? "h-[168px] shrink-0" : "h-1/2"} min-h-0 flex flex-col`}
                >
                  <RiskFlowMini
                    collapsed={combinedTapeCollapsed}
                    onToggleCollapsed={() =>
                      setCombinedTapeCollapsed(!combinedTapeCollapsed)
                    }
                    onNavigateToFeed={() => navigateTab("riskflow")}
                    onChatAlert={handleChatAlert}
                  />
                </section>
              </div>
            )}
            {combinedPanelCollapsed && (
              <div className="flex-1 flex flex-col items-center justify-center gap-3 p-2 bg-[var(--fintheon-surface)]">
                <div className="w-full max-w-[120px]">
                  <MinimalERMeter
                    resonance={normalizedCombinedPanelResonance}
                    pnl={combinedPanelPnl}
                    algoEnabled={combinedPanelAlgoEnabled}
                  />
                </div>
                <div className="w-full max-w-[120px]">
                  <MinimalTapeWidget />
                </div>
              </div>
            )}
          </div>
        </div>,
      );
    }
    // For 'tickers-only', no panels are shown (only floating widget)
  } else if (surfaceCapabilities.allowStrategium) {
    // When TopStepX is disabled: right stack = Mission Control + collapsible RiskFlow
    const hideRightPanel =
      showRefinement ||
      activeTab === "analysis" ||
      activeTab === "econ" ||
      activeTab === "narrative" ||
      activeTab === "apparatus" ||
      activeTab === "performance" ||
      activeTab === "proposals" ||
      activeTab === "settings";
    if (!hideRightPanel) {
      strategiumRailVisible = !missionControlCollapsed;
      rightPanels.push(
        // [claude-code 2026-04-29] S49: Strategium now uses a slide-out drawer
        // transition (like ChatPanel). Always rendered in DOM when not hidden by
        // active-tab guard; width + opacity transition handles open/close.
        <div
          key="right-stack"
          className={`h-full flex flex-col overflow-hidden transition-all duration-300 ease-in-out ${
            missionControlCollapsed
              ? "w-0 opacity-0 pointer-events-none invisible"
              : "w-[min(380px,42vw)] opacity-100"
          }`}
        >
          <div className="fintheon-side-surface fintheon-shell-side-panel fintheon-drawer-right flex-1 min-h-0 flex flex-col bg-[var(--fintheon-surface)] border-r border-[var(--fintheon-accent)]/20">
            {/* Widgets pane — shown in balanced + widgetsOnly.
                  [claude-code 2026-04-24] min-h-0 is CRITICAL in widgetsOnly:
                  without it, flex-1 + inner content force the pane taller than
                  the viewport and push the RiskFlow peek-footer off-screen. */}
            {strategiumPaneMode !== "feedOnly" && (
              <div
                className={`${
                  strategiumPaneMode === "widgetsOnly"
                    ? "flex-1 min-h-0"
                    : riskFlowCollapsed
                      ? "flex-1 min-h-0"
                      : "h-1/2 min-h-0"
                } flex flex-col transition-all duration-300 bg-[var(--fintheon-surface)] relative`}
              >
                <div className="flex-1 min-h-0 overflow-y-auto">
                  <div className="p-3 h-full">
                    {renderMissionControl(() =>
                      setMissionControlCollapsed(true),
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* RiskFlow pane — shown in balanced + feedOnly (and only when not fully-collapsed-in-feedOnly) */}
            {strategiumPaneMode !== "widgetsOnly" &&
              !(strategiumPaneMode === "feedOnly" && riskFlowCollapsed) && (
                <div
                  className={`${
                    strategiumPaneMode === "feedOnly"
                      ? "flex-1"
                      : riskFlowCollapsed
                        ? "h-[168px] shrink-0"
                        : "h-1/2"
                  } flex flex-col transition-all duration-300 relative`}
                >
                  <div className="flex-1 min-h-0 overflow-y-auto">
                    <RiskFlowMini
                      collapsed={riskFlowCollapsed}
                      onToggleCollapsed={() => {
                        // [S37] Balanced-mode collapse now reaches the 168px mini card instead
                        // of jumping to widgetsOnly (which fully hid the feed and left the user
                        // unable to restore). In each mode we toggle the riskFlowCollapsed flag
                        // directly — balanced + collapsed = 168px mini, feedOnly + collapsed
                        // = peek-footer, widgetsOnly still relies on its own peek-footer.
                        if (strategiumPaneMode === "feedOnly") {
                          setRiskFlowCollapsed(true);
                        } else {
                          setRiskFlowCollapsed((v) => !v);
                        }
                      }}
                      onNavigateToFeed={() => navigateTab("riskflow")}
                      onChatAlert={handleChatAlert}
                    />
                  </div>
                </div>
              )}

            {/* RiskFlow peek-footer — visible whenever the feed is fully hidden so users can always bring it back */}
            {(strategiumPaneMode === "widgetsOnly" ||
              (strategiumPaneMode === "feedOnly" && riskFlowCollapsed)) && (
              <StrategiumPeekBar
                variant="footer"
                label="RiskFlow"
                unreadCount={
                  riskFlowAlerts.filter((a: { id: string }) => !isSeen(a.id))
                    .length
                }
                onRestore={() => {
                  if (strategiumPaneMode === "widgetsOnly") {
                    updateStrategiumPaneMode("balanced");
                  } else {
                    setRiskFlowCollapsed(false);
                  }
                }}
              />
            )}
          </div>
        </div>,
      );
    }
  }

  const chatRailVisible = !surfaceCapabilities.isMobile && showChat;
  const shellRailGeometry = resolveShellRailGeometry([
    shellRailDescriptorFor(
      "left-underlay",
      false,
      "var(--fintheon-shell-left-underlay-width, 0px)",
    ),
    shellRailDescriptorFor(
      "cao-conversation-history",
      caoHistoryRailOpen,
      surfaceCapabilities.isMobile
        ? "min(292px, calc(100vw - 72px))"
        : "min(320px, 32vw)",
    ),
    shellRailDescriptorFor(
      "strategium",
      strategiumRailVisible,
      iframeModeActive && combinedPanelCollapsed ? "4rem" : "min(380px, 42vw)",
    ),
    shellRailDescriptorFor("cao-chat", chatRailVisible, "min(420px, 42vw)"),
  ] satisfies ShellRailDescriptor[]);
  const mainSurfaceRadiusClass = shellRailGeometry.mainSurfaceRadiusClass;

  const mobileContentStyle: React.CSSProperties | undefined = mobileDrawerMode
    ? {
        transform: mobileDrawerOpen
          ? "translateX(min(292px, calc(100vw - 72px)))"
          : "translateX(0)",
        filter: mobileDrawerOpen ? "brightness(0.72)" : "none",
        transition:
          "transform 330ms cubic-bezier(0.32, 0.72, 0, 1), filter 330ms cubic-bezier(0.32, 0.72, 0, 1)",
        willChange: "transform, filter",
      }
    : undefined;
  const shellBodyStyle = {
    ...shellRailGeometry.cssVars,
    ...mobileContentStyle,
  } as React.CSSProperties;
  const hideMobileQuickFooter =
    mobileDrawerMode &&
    activeTab === "analysis" &&
    mobileConsiliumView === "chat" &&
    mobileChatComposerFocused;

  return (
    <ScheduleProvider>
      <YouTubeMiniplayerProvider>
        <div
          data-surface-runtime={surfaceCapabilities.runtime}
          data-surface-form-factor={surfaceCapabilities.formFactor}
          data-surface-platform={
            typeof window !== "undefined"
              ? (window.electron?.platform ?? "web")
              : "web"
          }
          data-mobile-chat-composer-focused={
            hideMobileQuickFooter ? "true" : undefined
          }
          className={`fintheon-app-shell fintheon-ios27-shell relative h-screen w-full overflow-hidden flex flex-col bg-[var(--fintheon-bg)] text-white ${iframeModeActive ? "topstepx-active" : ""}`}
        >
          <GlassFilter />
          {mobileDrawerMode && (
            <MobileUnderlayDrawer
              open={mobileDrawerOpen}
              activeTab={activeTab}
              activeConsiliumView={mobileConsiliumView}
              onClose={() => setMobileDrawerOpen(false)}
              onTabChange={(tab) => {
                setShowRefinement(false);
                handleTabChange(tab as NavTab);
              }}
              onConsiliumView={openConsiliumLiteView}
              onNotificationCenterToggle={openBulletinInbox}
              onLogout={handleLogout}
            />
          )}
          <div className="relative z-10 shrink-0" style={mobileContentStyle}>
            <TopHeader
              topStepXEnabled={iframeModeActive}
              onTopStepXToggle={
                surfaceCapabilities.allowCustomIframes
                  ? handleBrowserEnable
                  : undefined
              }
              onTopStepXDisable={
                surfaceCapabilities.allowCustomIframes
                  ? handleBrowserToggle
                  : undefined
              }
              selectedPlatform={selectedPlatform}
              onPlatformSelect={setSelectedPlatform}
              layoutOption={layoutOption}
              onLayoutOptionChange={setLayoutOption}
              chatOpen={showChat}
              onChatToggle={() =>
                setShowChat((prev) => {
                  // Opening chat in Castra → auto-switch to Zen so panels don't fight for space
                  if (
                    !prev &&
                    iframeModeActive &&
                    layoutOption === "combined"
                  ) {
                    setLayoutOption("tickers-only");
                  }
                  return !prev;
                })
              }
              activeTab={activeTab}
              tabHistory={tabHistory}
              historyIndex={historyIndex}
              onBack={goBack}
              onForward={goForward}
              hideBranding={iframeModeActive && sidebarOverlayVisible}
              psychAssistHeadingWidget={
                surfaceCapabilities.allowPsychAssist &&
                iframeModeActive &&
                layoutOption === "tickers-only" &&
                psychAssistTarget === "header" &&
                !showEconCountdown ? (
                  <PsychAssistDockable
                    target="header"
                    onDockToHeader={() => setPsychAssistTarget("header")}
                    onUndockToFloating={() => setPsychAssistTarget("floating")}
                  />
                ) : undefined
              }
              econCountdownWidget={
                showEconCountdown ? (
                  <EconCountdownWidget
                    visible={showEconCountdown}
                    event={econCountdownCandidate}
                    ivData={ivData}
                    ivLoading={ivLoading}
                    onDismiss={() =>
                      setDismissedEconCountdownWindowId(
                        econCountdownCandidate?.dayPlanWindowId ?? null,
                      )
                    }
                  />
                ) : undefined
              }
              latestReleasedEconEvent={latestReleasedEconEvent}
              compactLevel={compactLevel}
              allowCustomIframes={surfaceCapabilities.allowCustomIframes}
              surfaceCapabilities={surfaceCapabilities}
              mobileDrawerOpen={mobileDrawerOpen}
              onMobileDrawerToggle={() => setMobileDrawerOpen((v) => !v)}
              /* [claude-code 2026-04-24] performanceChatWidget removed — orb is
               the only voice trigger now. */
            />
          </div>

          {/* S14-T6: Peers panel removed — team status is now in footer Team tab */}

          <div
            className="fintheon-shell-body flex-1 overflow-hidden relative bg-[var(--fintheon-surface)]"
            data-shell-left-rails={shellRailGeometry.activeLeftRailIds.join(
              " ",
            )}
            data-shell-right-rails={shellRailGeometry.activeRightRailIds.join(
              " ",
            )}
            onTouchStart={handleShellTouchStart}
            onTouchEnd={handleShellTouchEnd}
            style={shellBodyStyle}
          >
            <div className="relative flex h-full w-full overflow-hidden">
              <div
                className={
                  iframeModeActive || mobileDrawerMode
                    ? "hidden"
                    : "relative shrink-0 transition-[width] duration-300 ease-in-out"
                }
              >
                <NavSidebar
                  activeTab={activeTab}
                  onTabChange={(tab) => {
                    setShowRefinement(false);
                    handleTabChange(tab);
                  }}
                  onLogout={handleLogout}
                  topStepXEnabled={iframeModeActive}
                  onOverlayVisibilityChange={setSidebarOverlayVisible}
                  onNotificationCenterToggle={openBulletinInbox}
                  onRefinementClick={() => setShowRefinement((v) => !v)}
                  refinementEnabled={refinementEnabled}
                  refinementActive={showRefinement}
                  capabilities={surfaceCapabilities}
                />
              </div>
              {/* Left Panels */}
              {leftPanels.length > 0 && (
                <div className="flex">{leftPanels}</div>
              )}

              {/* Center Content - TopStepX or Main Content with crossfade. */}
              <div
                className={`fintheon-main-surface z-10 flex-1 overflow-hidden relative min-w-0 flex flex-col border border-[var(--fintheon-accent)]/20 bg-[var(--fintheon-bg)] ${mainSurfaceRadiusClass}`}
                data-active-tab={activeTab}
                data-has-right-rail={
                  shellRailGeometry.activeRightRailIds.length > 0
                    ? "true"
                    : "false"
                }
              >
                {/* Timeline overlay — slides over browser, does not affect iframe sizing */}
                <TimelineOverlay
                  open={timelineOverlayOpen}
                  onClose={() => setTimelineOverlayOpen(false)}
                />
                {iframeModeActive && !timelineOverlayOpen && (
                  <TimelineToggleButton
                    onClick={() => setTimelineOverlayOpen(true)}
                  />
                )}

                {/* Browser layer */}
                {iframeModeActive && (
                  <div
                    className={`absolute inset-0 z-10 ${isStone ? "bg-black" : ""} ${browserVisible ? "animate-browser-in" : "animate-browser-out"}`}
                  >
                    <TradingBrowser
                      primaryPlatform={selectedPlatform}
                      onPrimaryPlatformChange={setSelectedPlatform}
                      secondaryPlatform={secondaryPlatform}
                      onSecondaryPlatformChange={setSecondaryPlatform}
                      splitViewEnabled={splitBrowserView}
                      onSplitViewEnabledChange={setSplitBrowserView}
                      allowSplitView={iframeModeActive}
                    />
                  </div>
                )}

                {/* Main content layer */}
                <div
                  className={`h-full relative flex-1 flex flex-col ${iframeModeActive ? "pointer-events-none" : ""}`}
                  style={{
                    opacity: iframeModeActive ? 0 : 1,
                    transition: "opacity 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                >
                  <TabRenderer
                    activeTab={activeTab}
                    tabTransitioning={tabTransitioning}
                    prevTab={prevTab}
                    showRefinement={showRefinement}
                    navigateTab={navigateTab}
                    onChatAlert={handleChatAlert}
                    capabilities={surfaceCapabilities}
                  />
                </div>
              </div>

              {/* Right Panels */}
              {rightPanels.length > 0 && (
                <div className="flex">{rightPanels}</div>
              )}

              {/* Global chat panel - layout rail, not an overlay. */}
              {!surfaceCapabilities.isMobile && (
                <ChatPanel
                  showChat={showChat}
                  onClose={() => setShowChat(false)}
                  navigateTab={(tab) => navigateTab(tab as NavTab)}
                  pipAvailable={iframeModeActive && showChat}
                  onPopOut={popOutCaoChat}
                />
              )}

              {/* Floating Widget */}
              {showFloatingWidget && surfaceCapabilities.allowStrategium && (
                <FloatingWidget
                  ivData={ivData}
                  ivLoading={ivLoading}
                  layoutOption={layoutOption}
                  onClose={() => {}}
                />
              )}

              {/* Zen Layout: dockable PsychAssist widget (float ↔ header) */}
              {iframeModeActive &&
                surfaceCapabilities.allowPsychAssist &&
                layoutOption === "tickers-only" &&
                psychAssistTarget === "floating" && (
                  <PsychAssistDockable
                    target="floating"
                    onDockToHeader={() => setPsychAssistTarget("header")}
                    onUndockToFloating={() => setPsychAssistTarget("floating")}
                  />
                )}

              {/* Panel Notification Widgets */}
              {surfaceCapabilities.allowStrategium &&
                showMissionControlNotification && (
                  <PanelNotificationWidget
                    panelName="Mission Control"
                    position="bottom-left"
                    onRestore={() => {
                      setMissionControlPosition("right");
                      setShowMissionControlNotification(false);
                    }}
                    onDismiss={() => setShowMissionControlNotification(false)}
                  />
                )}
              {surfaceCapabilities.allowStrategium && showTapeNotification && (
                <PanelNotificationWidget
                  panelName="RiskFlow"
                  position="bottom-right"
                  onRestore={() => {
                    setTapePosition("right");
                    setShowTapeNotification(false);
                  }}
                  onDismiss={() => setShowTapeNotification(false)}
                />
              )}

              {/* YouTube floating miniplayer — persists independent of TradingBrowser */}
              {showYouTubeMiniplayer && (
                <YouTubeMiniplayer
                  onClose={() => {
                    setShowYouTubeMiniplayer(false);
                    try {
                      localStorage.setItem(
                        "fintheon:yt-miniplayer-open",
                        "false",
                      );
                    } catch {
                      /* ignore */
                    }
                  }}
                />
              )}

              {!surfaceCapabilities.isMobile && (
                <CaoPipComposer
                  open={
                    (caoPipOpen && iframeModeActive) ||
                    Boolean(quickAnnotateContext)
                  }
                  onRestoreRail={restoreCaoRail}
                  quickAnnotateContext={quickAnnotateContext}
                  onCloseQuickAnnotate={closeQuickAnnotate}
                />
              )}
              {!surfaceCapabilities.isMobile && <RiskFlowAlertPresenter />}
              {mobileDrawerMode && mobileDrawerOpen && (
                <button
                  type="button"
                  aria-label="Close mobile menu"
                  className="absolute inset-0 z-[80] cursor-default bg-transparent"
                  onClick={() => setMobileDrawerOpen(false)}
                />
              )}
            </div>
          </div>

          {surfaceCapabilities.allowFooterToolbar && <SessionCountdownWidget />}

          {mobileDrawerMode && !hideMobileQuickFooter && (
            <MobileQuickFooter
              activeTab={activeTab}
              activeConsiliumView={mobileConsiliumView}
              style={mobileContentStyle}
              onTabChange={(tab) => {
                setShowRefinement(false);
                handleTabChange(tab as NavTab);
              }}
              onConsiliumView={openConsiliumLiteView}
            />
          )}

          {surfaceCapabilities.allowFooterToolbar && (
            <FooterToolbar
              compactLevel={compactLevel}
              topStepXEnabled={iframeModeActive}
              primaryPlatform={selectedPlatform}
              onPrimaryPlatformChange={setSelectedPlatform}
              secondaryPlatform={secondaryPlatform}
              onSecondaryPlatformChange={setSecondaryPlatform}
              splitViewEnabled={splitBrowserView}
              onSplitViewToggle={() => setSplitBrowserView((v) => !v)}
              allowSplitView={iframeModeActive}
              onPowerOff={handleBrowserToggle}
              allowCustomIframes={surfaceCapabilities.allowCustomIframes}
            />
          )}

          {/* Preload iframes — hidden, loads TopStepX + Research in background for instant tab switch */}
          {surfaceCapabilities.allowCustomIframes && !iframeModeActive && (
            <div
              style={{
                position: "fixed",
                left: "-9999px",
                width: "1px",
                height: "1px",
                overflow: "hidden",
              }}
            >
              <EmbeddedBrowserFrame
                title="TopStepX (preload)"
                src="https://www.topstepx.com"
              />
              <EmbeddedBrowserFrame
                title="Research (preload)"
                src={iframeUrls.research || ""}
              />
            </div>
          )}

          {/* Global overlays */}
          <NativeTitleTooltipBridge />
          <SearchModal
            open={showSearchModal}
            onClose={() => setShowSearchModal(false)}
            onNavigateTab={(tab) => navigateTab(tab as NavTab)}
          />
          <WeeklyWalkthroughCue
            walkthrough={activeWalkthrough}
            onClose={closeWalkthrough}
            onReplay={replayWalkthrough}
          />
          <DeveloperCaoBridge />
          <NarrativeFlowDesktopBridge />
          <DeveloperDebriefPopup />
          <WhatsNewButton />

          {/* First-time user tour + interview + setup wizard */}
          <FirstTimeTour
            capabilities={surfaceCapabilities}
            onNavigate={(tab) => navigateTab(tab as NavTab)}
          />
        </div>
      </YouTubeMiniplayerProvider>
    </ScheduleProvider>
  );
}
